import type { ReactNode } from 'react'
import type { OutcomeCount, PortTag, RunSheet, ServiceRow, TargetRow, TargetSheet } from '../../api/admRuns'

/**
 * Bucatile pe care le impart cele trei ecrane de scanari - lista, fisa unei
 * scanari si programarile.
 *
 * Intr-un singur loc fiindca aceleasi trei lucruri se deseneaza pe toate:
 * pastila starii, bara de progres si sertarul care se deschide in rand. In
 * portal erau in `public/js/adm.js` PLUS in trei machete de PHP, iar sertarul
 * arata deja altfel decat celula de deasupra lui.
 *
 * Clasele sunt cele ale portalului (`public/css/adm.css`), deci pastilele si
 * barele arata la fel ca inainte de mutare.
 */

/** Textul unei celule, scapat - `admEscape()` din adm.js. */
export function esc(value: unknown): string {
  const box = document.createElement('div')
  box.textContent = String(value ?? '')
  return box.innerHTML
}

/** Pastila starii unei scanari - `statePill()` din adm.js. */
export const STATE_PILL: Record<number, string> = {
  1: 'adm-pill-running',
  2: 'adm-pill-running',
  3: 'adm-pill-ok',
  4: 'adm-pill-warn',
  5: 'adm-pill-bad',
}

/**
 * Culoarea capului de pagina, dupa cum s-a incheiat scanarea, ca o cadere sa se
 * vada rosie inainte sa fie citit vreun cuvant.
 */
export const RUN_TONE: Record<number, string> = {
  1: 'blue-hoki',
  2: 'blue-hoki',
  3: 'green-mode',
  4: 'yellow',
  5: 'red-intense',
}

/**
 * Un sfarsit e bun, nepasator sau rau; culoarea o spune fara ca cititorul sa
 * stie pe de rost cele sapte etichete.
 */
export const OUTCOME_TONE: Record<number, string> = {
  0: 'adm-pill-running',
  1: 'adm-pill-warn',
  2: 'adm-pill-warn',
  3: 'adm-pill-warn',
  4: 'adm-pill-ok',
  5: 'adm-pill-ok',
  6: 'adm-pill-ok',
  7: '',
}

export function statePillHtml(state: number, label: string): string {
  return `<span class="adm-pill ${STATE_PILL[state] ?? ''}">${esc(label)}</span>`
}

/** Bara de progres a unei scanari - `progressCell()` din adm.js. */
export function progressCellHtml(row: { targetsTotal: number; targetsResponded: number; progress: number }): string {
  /* O scanare fara numar de tinte e chiar la inceput. Zero la suta acolo s-ar
     citi "blocata", ceea ce e altceva. */
  if (!row.targetsTotal) return '<span class="adm-muted">preparing…</span>'

  return (
    `<div class="adm-progress"><span style="width:${row.progress}%"></span>` +
    `<em>${row.targetsResponded} / ${row.targetsTotal}</em></div>`
  )
}

/** O lista scurta ca pastile, cu numarul celor ramase - `admPills()` din adm.js. */
export function pillsHtml(items: string[], more: number): string {
  if (!items.length) return ''
  const pills = items.map((item) => `<span class="adm-pill">${esc(item)}</span> `).join('')
  return more > 0 ? `${pills}<span class="adm-muted">+${more}</span>` : pills
}

/** Text pus intr-un atribut (`title="..."`): `esc()` lasa ghilimelele cum sunt. */
export function escAttr(value: unknown): string {
  return esc(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

/**
 * Porturile deschise ca etichete mici: numarul pe eticheta, serviciul la hover
 * (`title`). Cand randul vine fara etichete (un raspuns mai vechi), cade pe
 * lista de text.
 */
export function portTagsHtml(row: { portTags?: PortTag[]; ports: string[]; portsMore: number }): string {
  const tags = row.portTags ?? []
  if (!tags.length) return pillsHtml(row.ports, row.portsMore)
  const pills = tags
    .map((tag) => `<span class="adm-pill adm-port" title="${escAttr(tag.title)}">${esc(tag.port)}</span> `)
    .join('')
  return row.portsMore > 0 ? `${pills}<span class="adm-muted">+${row.portsMore}</span>` : pills
}

/** Indiciile agentului (dc, smb, rdp...), stinse, sub porturi. */
export function hintsHtml(row: { hints?: string[]; hintsMore?: number }): string {
  const hints = row.hints ?? []
  if (!hints.length) return ''
  const more = (row.hintsMore ?? 0) > 0 ? ` +${row.hintsMore}` : ''
  return `<div class="adm-muted adm-hints" style="font-size:11px">${hints.map((h) => esc(h)).join(' · ')}${more}</div>`
}

/**
 * Ce s-a vazut pe adresa: felul ghicit (sau porturile, cand nu se poate
 * ghici), cu sistemul ghicit sub el. "Alive, not classified" ramane numai in
 * rezumatul pe sfarsituri.
 */
export function foundHtml(row: { foundLabel?: string; outcomeLabel: string; os?: string }): string {
  const label = esc(row.foundLabel || row.outcomeLabel)
  return row.os ? `${label}<div class="adm-muted adm-nowrap" style="font-size:11px">${esc(row.os)}</div>` : label
}

/**
 * Dusul si intorsul, intr-o celula - `admReplyCell()` din adm.js.
 *
 * Doua coloane pentru doua numere care se citesc mereu impreuna ar costa
 * latimea de care are nevoie sfarsitul. TTL-ul e stins fiindca e cifra care
 * sprijina: un raspuns iute la un TTL mic e un ruter care raspunde, nu o masina
 * iute.
 */
export function replyCellHtml(row: { pingMs: number | null; ttl: number | null }): string {
  const parts: string[] = []
  if (row.pingMs !== null) parts.push(`<span class="adm-nowrap">${esc(row.pingMs)} ms</span>`)
  if (row.ttl !== null) parts.push(`<span class="adm-muted adm-nowrap">ttl ${esc(row.ttl)}</span>`)
  return parts.join(' ')
}

/** Capetele coloanelor tabelului de adrese, pe cheie - `detail.php`. */
export const TARGET_HEADS: Record<string, { label: string; width?: number }> = {
  detail: { label: '', width: 50 },
  ip: { label: 'Address' },
  hostname: { label: 'Host name' },
  mac: { label: 'MAC' },
  manufacturer: { label: 'Made by' },
  ports: { label: 'Open ports', width: 190 },
  fingerprint: { label: 'Answered as', width: 190 },
  response: { label: 'Reply', width: 110 },
  outcome: { label: 'Outcome' },
  updated: { label: 'Updated' },
}

/** HTML-ul unei celule din tabelul de adrese, pe cheia coloanei. */
export function targetCellHtml(key: string, row: TargetRow): string {
  switch (key) {
    case 'detail':
      /* Desenat pe RAND, nu pe tabel: o adresa pe care agentul n-a amprentat-o
         n-are nimic in spatele iconitei, iar un "+" care deschide un sertar gol
         e mai rau decat niciun "+". */
      return row.hasDetail
        ? `<center class="show_details color-dark-grey pointer font-16" data-id="${row.ID}"><i class="fas fa-plus-circle"></i></center>`
        : ''
    case 'ip':
      return esc(row.ip)
    case 'hostname':
      /* Numele scurt, cu FQDN-ul dedesubt (wks02 / wks02.cecdemo.local). */
      return esc(row.hostname) + (row.fqdn ? `<div class="adm-muted" style="font-size:11px">${esc(row.fqdn)}</div>` : '')
    case 'mac':
      return esc(row.mac)
    case 'manufacturer':
      return esc(row.manufacturer)
    case 'ports':
      return portTagsHtml(row) + hintsHtml(row)
    case 'fingerprint':
      /* Ce s-a amprentat, ca felurile care au raspuns. Amanuntul - care port ce
         a spus - e la un clic in rand, deci celula poarta forma masinii, nu
         dovada pentru ea. */
      return pillsHtml(row.kinds, row.kindsMore)
    case 'response':
      return replyCellHtml(row)
    case 'outcome':
      return foundHtml(row)
    case 'updated':
      return esc(row.updatedAt)
    default:
      return ''
  }
}

/** Clasa coloanei, ca in `adm.js`. */
export function targetCellClass(key: string): string | undefined {
  switch (key) {
    case 'detail':
      return 'text-center'
    case 'ip':
      return 'adm-nowrap'
    case 'mac':
      return 'adm-nowrap adm-muted'
    case 'response':
      return 'adm-nowrap'
    case 'updated':
      return 'adm-nowrap adm-muted'
    default:
      return undefined
  }
}

/**
 * Cifrele unei scanari, ca in `runs/row_expand.php`.
 *
 * `ciCreated` / `ciUpdated` le socoteste serverul din 13 octombrie 2026
 * (agentul trimitea 0): aparate gasite noi in rularea asta si aparate deja
 * stiute, vazute din nou. O scanare nu scrie in CMDB - asta o face coada
 * Pending - deci eticheta spune "devices", nu "items": un 0 la "new" langa
 * "40 seen" inseamna "nimic nou", nu "n-a gasit nimic".
 */
function RunFigures({ run }: { run: RunSheet }) {
  return (
    <div className="adm-expand-figures">
      <div>
        <span className="adm-figure">{run.targetsResponded}</span>
        <span className="adm-figure-label">devices seen</span>
      </div>
      <div>
        <span className="adm-figure">{run.ciCreated}</span>
        <span className="adm-figure-label">new</span>
      </div>
      <div>
        <span className="adm-figure">{run.ciUpdated}</span>
        <span className="adm-figure-label">already known</span>
      </div>
      <div>
        <span className="adm-figure">{run.relationsCreated}</span>
        <span className="adm-figure-label">relations created</span>
      </div>
      <div>
        <span className={run.errorsCount > 0 ? 'adm-figure text-danger' : 'adm-figure'}>
          {run.errorsCount > 0 ? <a href={`/app/adm/errors?company_id=${run.companyId}`}>{run.errorsCount}</a> : 0}
        </span>
        <span className="adm-figure-label">failures</span>
      </div>
    </div>
  )
}

/**
 * Tot ce se stie despre o scanare, in randul ei - `runs/row_expand.php`.
 *
 * Fara file si fara coloana de acces rapid. Amandoua erau feluri de a incapea
 * mai mult decat incape, si niciunul nu si-a meritat clicul aici: ce vrea cineva
 * cand deschide o scanare e toata scanarea deodata. Doua din ele in spatele unei
 * file sunt doua necitite.
 */
export function RunDrawerBody({
  run,
  breakdown,
  rows,
  total,
}: {
  run: RunSheet
  breakdown: OutcomeCount[]
  rows: TargetRow[]
  total: number
}) {
  return (
    <div className="adm-expand">
      <div className="row">
        <div className="col-md-5">
          <h5 className="adm-expand-head">
            <i className="fas fa-info-circle"></i> The scan
          </h5>

          <table className="table table-condensed adm-list">
            <tbody>
              <tr>
                <td style={{ width: 130 }} className="text-muted">
                  State
                </td>
                <td>
                  <span className={`adm-pill ${STATE_PILL[run.state] ?? ''}`}>{run.stateLabel}</span>
                </td>
              </tr>
              <tr>
                <td className="text-muted">Customer</td>
                <td>{run.companyName}</td>
              </tr>
              <tr>
                <td className="text-muted">Type</td>
                <td>{run.typeLabel}</td>
              </tr>
              <tr>
                <td className="text-muted">Started by</td>
                <td>
                  {run.triggerLabel}
                  {run.startedByName ? ` — ${run.startedByName}` : ''}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Relay</td>
                <td>{run.relayName || '—'}</td>
              </tr>
              <tr>
                <td className="text-muted">Scope</td>
                <td>{run.rangeName || 'All ranges'}</td>
              </tr>
              <tr>
                <td className="text-muted">Ran for</td>
                <td>
                  {run.duration}
                  <div className="adm-muted" style={{ fontSize: 11 }}>
                    from {run.startedAt}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-md-7">
          <h5 className="adm-expand-head">
            <i className="fas fa-chart-bar"></i> What came back
          </h5>

          {run.targetsTotal === 0 ? (
            <div className="adm-muted">No addresses recorded for this run yet.</div>
          ) : (
            <div className="adm-progress" style={{ height: 22 }}>
              <span style={{ width: `${run.progress}%` }}></span>
              <em style={{ lineHeight: '22px' }}>
                {run.targetsResponded} of {run.targetsTotal} responded ({run.progress}%)
              </em>
            </div>
          )}

          <RunFigures run={run} />

          {breakdown.length > 0 ? (
            <>
              <p className="text-muted small" style={{ margin: '12px 0 4px' }}>
                What came of each address:
              </p>
              {breakdown.map((item) => (
                <span key={item.outcome} className={`adm-pill ${OUTCOME_TONE[item.outcome] ?? ''}`} style={{ margin: '0 4px 4px 0' }}>
                  {item.title} · {item.value.toLocaleString('en-US')}
                </span>
              ))}
            </>
          ) : null}
        </div>
      </div>

      <h5 className="adm-expand-head" style={{ marginTop: 16 }}>
        <i className="fas fa-network-wired"></i> Addresses{' '}
        {total > 0 ? (
          <span className="text-muted small">
            {total > rows.length ? `first ${rows.length} of ${total.toLocaleString('en-US')}` : total.toLocaleString('en-US')}
          </span>
        ) : null}
      </h5>

      {rows.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-network-wired"></i> No addresses have been recorded for this run.
        </div>
      ) : (
        <>
          <table className="table table-condensed adm-list">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Address</th>
                <th>Name</th>
                <th style={{ width: 150 }}>MAC</th>
                <th>Open ports</th>
                <th>What it looks like</th>
                <th style={{ width: 140 }}>Last touched</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.ID}>
                  <td className="adm-nowrap">{row.ip}</td>
                  <td>
                    {row.hostname || '—'}
                    {row.fqdn ? <div className="adm-muted" style={{ fontSize: 11 }}>{row.fqdn}</div> : null}
                    {/* Sub nume, nu intr-o coloana a lui: sertarul e o privire
                        de douazeci de randuri cu pagina intreaga la o legatura
                        dedesubt, deci o a sasea coloana goala pe cele mai multe
                        randuri ar costa latime pentru cateva. */}
                    {row.manufacturer.trim() !== '' ? (
                      <div className="adm-muted" style={{ fontSize: 11 }}>
                        {row.manufacturer}
                      </div>
                    ) : null}
                  </td>
                  <td className="adm-nowrap adm-muted">{row.mac || '—'}</td>
                  <td className="admFoundPorts">
                    <PortTags row={row} />
                    {(row.hints ?? []).length > 0 ? (
                      <div className="adm-muted" style={{ fontSize: 11 }}>
                        {row.hints.join(' · ')}
                        {row.hintsMore > 0 ? ` +${row.hintsMore}` : ''}
                      </div>
                    ) : null}
                  </td>
                  <td className="admFoundKind">
                    {row.foundLabel || row.outcomeLabel}
                    {/* Sistemul ghicit si TTL-ul, sub fel: dovada pe care s-a
                        sprijinit ghicitul, nu o coloana a ei. */}
                    {row.os ? (
                      <div className="adm-muted adm-nowrap" style={{ fontSize: 11 }}>
                        {row.os}
                      </div>
                    ) : null}
                  </td>
                  <td className="adm-nowrap adm-muted">{row.updatedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {total > rows.length ? (
            <p className="text-muted small" style={{ marginBottom: 0 }}>
              <a href={`/app/adm/run/${run.ID}`}>Open the run to search all {total.toLocaleString('en-US')} of them</a>.
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

/** Porturile unei adrese ca etichete mici, cu serviciul la hover. */
function PortTags({ row }: { row: TargetRow }) {
  const tags = row.portTags ?? []
  if (!tags.length && !row.ports.length) return <span className="adm-muted">—</span>
  return (
    <>
      {tags.length
        ? tags.map((tag) => (
            <span key={tag.port} className="adm-pill adm-port" title={tag.title} style={{ margin: '0 3px 3px 0' }}>
              {tag.port}
            </span>
          ))
        : row.ports.map((port) => (
            <span key={port} className="adm-pill" style={{ margin: '0 3px 3px 0' }}>
              {port}
            </span>
          ))}
      {row.portsMore > 0 ? <span className="adm-muted">+{row.portsMore}</span> : null}
    </>
  )
}

/**
 * Ce a citit scanarea de pe o adresa - `runs/target_expand.php`.
 *
 * Nimic de aici nu e o hotarare, deci nimic de aici nu e un buton. E o dovada.
 */
export function TargetDrawerBody({ target, rows, total }: { target: TargetSheet; rows: ServiceRow[]; total: number }) {
  /*
   * Ce a citit scanarea si nu e un port.
   *
   * Facut ca lista, nu scris ca randuri: fiecare din astea e gol pana pleaca
   * agentul, si un tabel cu sapte etichete goale e chiar ecranul stricat pe care
   * munca asta vrea sa-l evite. Primeste rand numai ce are valoare.
   */
  const facts: Array<[string, ReactNode]> = []

  if (target.manufacturer.trim() !== '') facts.push(['Made by', target.manufacturer])

  if (target.pingMs !== null || target.ttl !== null) {
    facts.push([
      'Reply',
      <>
        {target.pingMs !== null ? `${target.pingMs} ms` : null}{' '}
        {/* Numarul de salturi, langa dus-intors: un TTL mic pe un raspuns iute
            e un ruter in cale, nu o masina inceata. */}
        {target.ttl !== null ? <span className="adm-muted">ttl {target.ttl}</span> : null}
      </>,
    ])
  }

  if (target.uuid.trim() !== '') facts.push(['UUID', <span className="adm-nowrap">{target.uuid}</span>])

  /* Gazda virtuala, cand adresa s-a dovedit a fi un oaspete. Merita un rand al
     ei: o adresa care e o masina virtuala pe o gazda deja stiuta nu e o masina
     noua de cautat. */
  if (target.virtualHostUnique.trim() !== '' || target.virtualHostMac.trim() !== '') {
    facts.push([
      'Runs on',
      <>
        {target.virtualHostUnique}{' '}
        {target.virtualHostMac ? <span className="adm-muted adm-nowrap">{target.virtualHostMac}</span> : null}
      </>,
    ])
  }

  if (target.hints.length > 0) {
    facts.push([
      'Hints',
      <>
        {target.hints.map((hint) => (
          <span key={hint} className="adm-pill" style={{ margin: '0 4px 4px 0' }}>
            {hint}
          </span>
        ))}
      </>,
    ])
  }

  /* Neplafonate aici, spre deosebire de celula din care s-a venit: sertarul e
     locul in care cineva a venit sa vada lista intreaga. */
  if (target.ports.length > 0) {
    facts.push(['Open ports', <span className="adm-nowrap">{target.ports.join(', ')}</span>])
  }

  return (
    <div className="adm-expand">
      <div className="row">
        <div className="col-md-4">
          <h5 className="adm-expand-head">
            <i className="fas fa-fingerprint"></i> The address
          </h5>

          <table className="table table-condensed adm-list">
            <tbody>
              <tr>
                <td style={{ width: 110 }} className="text-muted">
                  Address
                </td>
                <td className="adm-nowrap">{target.ip}</td>
              </tr>
              <tr>
                <td className="text-muted">Host name</td>
                <td>
                  {target.hostname || '—'}
                  {target.fqdn ? <div className="adm-muted" style={{ fontSize: 11 }}>{target.fqdn}</div> : null}
                </td>
              </tr>
              <tr>
                <td className="text-muted">MAC</td>
                <td className="adm-nowrap adm-muted">{target.mac || '—'}</td>
              </tr>
              <tr>
                <td className="text-muted">Outcome</td>
                <td>{target.outcomeLabel}</td>
              </tr>
              {facts.map(([label, value]) => (
                <tr key={label}>
                  <td className="text-muted">{label}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="col-md-8">
          <h5 className="adm-expand-head">
            <i className="fas fa-plug"></i> What answered{' '}
            {total > 0 ? (
              <span className="text-muted small">
                {total > rows.length
                  ? `first ${rows.length} of ${total.toLocaleString('en-US')}`
                  : `${total.toLocaleString('en-US')}${total === 1 ? ' port' : ' ports'}`}
              </span>
            ) : null}
          </h5>

          {rows.length === 0 ? (
            <div className="adm-empty-inline">
              <i className="fas fa-plug"></i> Nothing was fingerprinted on this address.
            </div>
          ) : (
            <table className="table table-condensed adm-list">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Proto</th>
                  <th style={{ width: 70 }} className="adm-numeric">
                    Port
                  </th>
                  <th style={{ width: 210 }}>Kind</th>
                  <th>What it said</th>
                  <th style={{ width: 120 }}>Seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.proto}-${row.port}-${index}`}>
                    <td className="adm-nowrap adm-muted">{row.proto || '—'}</td>
                    <td className="adm-numeric">{row.port === null ? '—' : row.port}</td>
                    <td>
                      {row.kind === '' ? (
                        <span className="adm-muted">—</span>
                      ) : (
                        <>
                          <span className="adm-pill">{row.kind}</span>{' '}
                          {/* Un fel pe care agentul l-a invatat de cand s-a scris
                              lista se scrie totusi; vine doar fara lamurire. */}
                          {row.kindLabel ? (
                            <span className="adm-muted" style={{ fontSize: 11 }}>
                              {row.kindLabel}
                            </span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td>{row.summary}</td>
                    <td className="adm-nowrap adm-muted">{row.seenAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Descarcarea agentului, ultima si mica: e ce a scris scanerul, nu un
              camp in jurul caruia a gandit cineva un ecran. */}
          {target.serviceDetails.trim() !== '' ? (
            <>
              <h5 className="adm-expand-head" style={{ marginTop: 16 }}>
                <i className="fas fa-align-left"></i> What the scanner recorded
              </h5>
              <pre className="adm-muted" style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0 }}>
                {target.serviceDetails}
              </pre>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
